import { themeVar as colors } from "../design-tokens";
import type { SubjectFieldDetail, SubjectFieldState } from "./types";

/**
 * THE SUBJECT STEP'S CONTROLS THAT ARE NOT A PLAIN BOX (Q3, 2026-10-25).
 *
 * Everything here is presentation over the SAME grid state the step has always written: a value,
 * a source tag, a checked flag, and the override trail `ConfirmGrid.editField` keeps. No control
 * invents a value, no dropdown has a default, and nothing here reads or writes a figure the engine
 * prices from except by handing the agent's own choice back through `onChange`.
 */

/** The circled i beside a label. A real button: Tab reaches it, Enter and Space open it, Escape
 * closes it (the grid handles the key). Distinct from the provenance chip's trailing "ⓘ" glyph by
 * its position (beside the field name, not a figure) and its accessible name ("What is ...?"). */
export function InfoButton({ label, open, onToggle, panelId }: {
  label: string; open: boolean; onToggle: () => void; panelId: string;
}) {
  return (
    <button
      type="button"
      className="vb-infotip"
      aria-label={`What is ${label}?`}
      aria-expanded={open}
      aria-controls={panelId}
      onClick={onToggle}
    >
      i
    </button>
  );
}

// ------------------------------------------------------------------------------------------- //
// HOA                                                                                          //
// ------------------------------------------------------------------------------------------- //

const FREQ_WORDS: Record<NonNullable<SubjectFieldDetail["frequency"]>, string> = {
  monthly: "per month", quarterly: "per quarter", annually: "per year",
};

/** The one string the report prints for an HOA, composed from the agent's answers by string
 * handling only. "Yes" alone prints "Yes"; it is never `true`, because the report prints `true` as
 * "Mandatory" and that would assert something the agent did not say. No amount is ever invented. */
export function composeHoa(d: SubjectFieldDetail): string | boolean | null {
  if (d.answer === "no") return false;
  if (d.answer !== "yes") return null;
  const parts: string[] = [];
  const amt = (d.amount ?? "").trim();
  if (amt) {
    const clean = amt.replace(/^\$/, "");
    parts.push(`$${clean}${d.frequency ? ` ${FREQ_WORDS[d.frequency]}` : ""}`);
  } else if (d.frequency) {
    parts.push(FREQ_WORDS[d.frequency]);
  }
  if (d.kind) parts.push(d.kind);
  return parts.length ? parts.join(", ") : "Yes";
}

/** What a value that arrived from a source (the county, a file, later the MLS record) means for
 * the HOA question. A string is a yes with the source's own words; true is a yes; false is a no. */
export function hoaAnswerOf(state: SubjectFieldState): SubjectFieldDetail["answer"] | undefined {
  if (state.detail?.answer) return state.detail.answer;
  const v = state.value;
  if (v === false) return "no";
  if (v === true || (typeof v === "string" && v.trim() !== "")) return "yes";
  return undefined;
}

export function HoaControl({ state, className, onDetail }: {
  state: SubjectFieldState;
  className: string;
  /** Hands back the next detail; the grid writes the composed value and the override trail. */
  onDetail: (next: SubjectFieldDetail) => void;
}) {
  const answer = hoaAnswerOf(state);
  const d: SubjectFieldDetail = state.detail ?? {};
  const asRecorded =
    answer === "yes" && !state.detail && typeof state.value === "string" ? state.value : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <select
        className={className}
        data-empty={answer === undefined || undefined}
        data-edited={state.edited || undefined}
        aria-label="HOA"
        value={answer ?? ""}
        onChange={(e) => {
          const a = e.target.value as SubjectFieldDetail["answer"] | "";
          onDetail(a === "" ? {} : { ...d, answer: a });
        }}
      >
        <option value="">Not stated</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
        <option value="not_sure">Not sure</option>
      </select>
      {answer === "not_sure" && (
        <span style={{ fontSize: 11, color: colors.textSecondary }}>
          Left blank; “Not sure” is never written as “No”.
        </span>
      )}
      {answer === "yes" && (
        <>
          {asRecorded && (
            <span data-hoa-recorded style={{ fontSize: 11, color: colors.textSecondary }}>
              As recorded: {asRecorded}
            </span>
          )}
          <input
            className={className}
            type="number"
            min="0"
            value={d.amount ?? ""}
            placeholder="Amount ($)"
            aria-label="HOA amount in dollars"
            onWheel={(e) => e.currentTarget.blur()}
            onChange={(e) => onDetail({ ...d, answer: "yes", amount: e.target.value })}
          />
          <select
            className={className}
            aria-label="HOA frequency"
            data-empty={!d.frequency || undefined}
            value={d.frequency ?? ""}
            onChange={(e) => onDetail({ ...d, answer: "yes", frequency: (e.target.value || undefined) as SubjectFieldDetail["frequency"] })}
          >
            <option value="">Frequency not stated</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annually">Annually</option>
          </select>
          <select
            className={className}
            aria-label="HOA mandatory or voluntary"
            data-empty={!d.kind || undefined}
            value={d.kind ?? ""}
            onChange={(e) => onDetail({ ...d, answer: "yes", kind: (e.target.value || undefined) as SubjectFieldDetail["kind"] })}
          >
            <option value="">Mandatory or voluntary</option>
            <option value="mandatory">Mandatory</option>
            <option value="voluntary">Voluntary</option>
          </select>
        </>
      )}
    </div>
  );
}
